import React, { Suspense, useEffect } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter } from "react-router";
import { AuthProvider } from "@/components/auth/AuthProvider";
import { TenantProvider } from "@/contexts/TenantContext";
import { MultiTenantRouter } from "@/routing/MultiTenantRouter";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { Toaster } from "@/components/ui/Toast";
import { CommandPalette } from "@/components/search/CommandPalette";
import { useCommandPalette } from "@/hooks/useCommandPalette";
import { OnboardingProvider, Onboarding } from "@/components/onboarding";
import { GovernanceProvider } from "@/components/governance/GovernanceSlot";
import { I18nextProvider } from 'react-i18next';
import i18n from '@/i18n';
import { initWebVitals, logPerformanceSummary } from "@/lib/performance";
import { FocusManager } from "@/components/accessibility/FocusManager";
import { GlobalKeyboardShortcuts } from "@/components/accessibility/GlobalKeyboardShortcuts";
import { RouteAnnouncer } from "@/components/accessibility/RouteAnnouncer";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Optimize query caching for better performance
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes (formerly cacheTime)
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

const AppContent = () => {
  const { isOpen, close, toggle } = useCommandPalette();

  // Initialize performance monitoring
  useEffect(() => {
    if (process.env.NODE_ENV === "production") {
      initWebVitals();
    }

    if (process.env.NODE_ENV === "development") {
      window.addEventListener("load", () => {
        setTimeout(() => {
          logPerformanceSummary();
        }, 2000);
      });
    }
  }, []);

  return (
    <>
      <Toaster />
      <GlobalKeyboardShortcuts onToggleCommandPalette={toggle} />
      <RouteAnnouncer />
      <CommandPalette open={isOpen} onClose={close} />
      <Onboarding />
      <Suspense fallback={
        <div className="min-h-screen flex items-center justify-center bg-background">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent mx-auto mb-4"></div>
            <p className="text-muted-foreground">Loading application...</p>
          </div>
        </div>
      }>
        <MultiTenantRouter />
      </Suspense>
    </>
  );
};

const App = () => (
  <I18nextProvider i18n={i18n}>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <ErrorBoundary>
          <TenantProvider>
            <AuthProvider>
              <OnboardingProvider>
                <GovernanceProvider>
                  <FocusManager>
                    <AppContent />
                  </FocusManager>
                </GovernanceProvider>
              </OnboardingProvider>
            </AuthProvider>
          </TenantProvider>
        </ErrorBoundary>
      </BrowserRouter>
    </QueryClientProvider>
  </I18nextProvider>
);

export default App;